import { type FC, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Table, Select, Input, Spin, Empty, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { useCustomerConfig, useTpoGenConfig } from '../../../context/CustomerConfigContext';
import type { KpiOption } from '../../../PlanDetail/MetricPage/config/kpiCatalog';
import type { KpiBreakdownRow, ScenarioPlan } from '../../types';
import {
    SCENARIO_BASE_COLUMN_KEY,
    TPO_SCENARIO_BREAKDOWN_EMPTY,
    TPO_SCENARIO_BREAKDOWN_LEVEL_SELECT,
    TPO_SCENARIO_BREAKDOWN_LOADING,
    TPO_SCENARIO_BREAKDOWN_SEARCH_INPUT,
    TPO_SCENARIO_BREAKDOWN_SECTION,
    TPO_SCENARIO_BREAKDOWN_TABLE,
    TPO_SCENARIO_BREAKDOWN_TITLE,
    tpoOptionTestId,
    tpoScenarioBreakdownRowTestId,
    tpoTableColTestId,
} from '../../../config/testIds';
import { PLAIN_NUMBER_FORMAT } from '../../../config/metrics';
import { formatMetricValue } from '../../../utils/formatMetricValue';
import DeltaIndicator from './DeltaIndicator';

const ANT_TABLE_HEADER_HEIGHT = 55;
const BOTTOM_PADDING = 16;
const MIN_TABLE_HEIGHT = 120;

/** Restates AntD's own default so the id is the only thing this overrides. */
const BREAKDOWN_TABLE_LOCALE = {
    emptyText: (
        <div data-testid={TPO_SCENARIO_BREAKDOWN_EMPTY}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
    ),
};

const BREAKDOWN_SURFACE = 'scenario_breakdown';

interface KpiBreakdownTableProps {
    kpi: KpiOption;
    baseScenario: ScenarioPlan | null;
    comparisonScenarios: ScenarioPlan[];
    rows: KpiBreakdownRow[];
    selectedKpiLevel: string;
    searchTerm: string;
    isFetching: boolean;
    showPercent?: boolean;
    onKpiLevelChange: (level: string) => void;
    onSearchChange: (search: string) => void;
}

const KpiBreakdownTable: FC<KpiBreakdownTableProps> = ({
    kpi,
    baseScenario,
    comparisonScenarios,
    rows,
    selectedKpiLevel,
    searchTerm,
    isFetching,
    showPercent,
    onKpiLevelChange,
    onSearchChange,
}) => {
    const { scenarioComparisonConfig, labels } = useTpoGenConfig();
    // The whole breakdown is one KPI, so its format is resolved once.
    const { metricFormatByKey } = useCustomerConfig();
    const kpiFormat = metricFormatByKey.get(kpi.key) ?? PLAIN_NUMBER_FORMAT;

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

    const recalcHeight = useCallback(() => {
        const el = tableWrapperRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const bodyHeight = window.innerHeight - top - ANT_TABLE_HEADER_HEIGHT - BOTTOM_PADDING;
        setTableScrollY(Math.max(MIN_TABLE_HEIGHT, bodyHeight));
    }, []);

    useEffect(() => {
        recalcHeight();
        window.addEventListener('resize', recalcHeight);
        return () => window.removeEventListener('resize', recalcHeight);
    }, [recalcHeight]);

    const kpiLevelOptions = useMemo(
        () =>
            scenarioComparisonConfig.kpiBreakdownLevels.map((l) => ({
                label: l.label,
                value: l.key,
                'data-testid': tpoOptionTestId(TPO_SCENARIO_BREAKDOWN_LEVEL_SELECT, l.key),
            })),
        [scenarioComparisonConfig.kpiBreakdownLevels],
    );

    const activeLevelLabel = useMemo(
        () =>
            scenarioComparisonConfig.kpiBreakdownLevels.find((l) => l.key === selectedKpiLevel)?.label ??
            selectedKpiLevel,
        [scenarioComparisonConfig.kpiBreakdownLevels, selectedKpiLevel],
    );

    const allPlans = useMemo(() => {
        const plans: (ScenarioPlan & { isBase: boolean })[] = [];
        if (baseScenario) plans.push({ ...baseScenario, isBase: true });
        for (const s of comparisonScenarios) {
            plans.push({ ...s, isBase: false });
        }
        return plans;
    }, [baseScenario, comparisonScenarios]);

    const columns: ColumnsType<KpiBreakdownRow> = useMemo(() => {
        const cols: ColumnsType<KpiBreakdownRow> = [
            {
                title: activeLevelLabel,
                dataIndex: selectedKpiLevel,
                key: selectedKpiLevel,
                width: 250,
                fixed: 'left',
                onHeaderCell: () =>
                    ({
                        'data-testid': tpoTableColTestId(BREAKDOWN_SURFACE, selectedKpiLevel),
                    } as React.ThHTMLAttributes<HTMLTableCellElement>),
                sorter: (a, b) => {
                    const aVal = String(a[selectedKpiLevel] ?? '');
                    const bVal = String(b[selectedKpiLevel] ?? '');
                    return aVal.localeCompare(bVal);
                },
            },
        ];

        for (const plan of allPlans) {
            const columnKey = plan.isBase ? SCENARIO_BASE_COLUMN_KEY : plan.planId;
            const headerText = plan.isBase ? `${labels.comparisonReport.basePrefix}: ${plan.planName}` : plan.planName;
            cols.push({
                title: <span className="block truncate">{headerText}</span>,
                key: plan.planId,
                width: 280,
                ellipsis: { showTitle: false },
                onHeaderCell: () =>
                    ({
                        'data-testid': tpoTableColTestId(BREAKDOWN_SURFACE, columnKey),
                    } as React.ThHTMLAttributes<HTMLTableCellElement>),
                sorter: (a, b) => {
                    const aKpi = a.kpis?.[0];
                    const bKpi = b.kpis?.[0];
                    const aVal = plan.isBase ? aKpi?.value1 ?? null : aKpi?.comparisons[plan.planId]?.value ?? null;
                    const bVal = plan.isBase ? bKpi?.value1 ?? null : bKpi?.comparisons[plan.planId]?.value ?? null;
                    return (aVal ?? 0) - (bVal ?? 0);
                },
                render: (_: unknown, record: KpiBreakdownRow) => {
                    const kpiData = record.kpis?.[0];
                    if (!kpiData) return <span className="text-[#D9D9D9]">-</span>;

                    const raw = plan.isBase ? kpiData.value1 : kpiData.comparisons[plan.planId]?.value ?? null;

                    if (raw == null) return <span className="text-[#D9D9D9]">-</span>;

                    const delta = plan.isBase ? null : kpiData.comparisons[plan.planId]?.delta ?? null;

                    return (
                        <div className="flex items-center gap-2">
                            <span>{formatMetricValue(raw, kpiFormat)}</span>
                            <DeltaIndicator
                                delta={delta}
                                isPercent={showPercent}
                                format={kpiFormat}
                                colors={scenarioComparisonConfig.deltaColors}
                            />
                        </div>
                    );
                },
            });
        }

        return cols;
    }, [
        allPlans,
        selectedKpiLevel,
        activeLevelLabel,
        showPercent,
        labels.comparisonReport.basePrefix,
        scenarioComparisonConfig.deltaColors,
        kpiFormat,
    ]);

    return (
        <div className="mt-6" data-testid={TPO_SCENARIO_BREAKDOWN_SECTION}>
            <div className="border-t border-[#E4E4E7] mb-4" />
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#18181B]" data-testid={TPO_SCENARIO_BREAKDOWN_TITLE}>
                    {labels.comparisonReport.kpiBreakdownTitle(kpi.name)}
                </h3>
                <div className="flex items-center gap-3">
                    <Select
                        style={{ width: 160 }}
                        options={kpiLevelOptions}
                        value={selectedKpiLevel}
                        onChange={onKpiLevelChange}
                        size="middle"
                        data-testid={TPO_SCENARIO_BREAKDOWN_LEVEL_SELECT}
                    />
                    <Input
                        placeholder={labels.comparisonReport.search}
                        prefix={<SearchOutlined />}
                        style={{ width: 220 }}
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        size="middle"
                        allowClear
                        data-testid={TPO_SCENARIO_BREAKDOWN_SEARCH_INPUT}
                    />
                </div>
            </div>
            <div ref={tableWrapperRef} data-testid={isFetching ? TPO_SCENARIO_BREAKDOWN_LOADING : undefined}>
                <Spin spinning={isFetching} size="small">
                    <Table<KpiBreakdownRow>
                        columns={columns}
                        dataSource={rows}
                        rowKey={(record, index) => `${String(record[selectedKpiLevel] ?? '')}-${index}`}
                        size="small"
                        scroll={{ x: 'max-content', y: tableScrollY }}
                        pagination={false}
                        bordered
                        locale={BREAKDOWN_TABLE_LOCALE}
                        onRow={(record) =>
                            ({
                                'data-testid': tpoScenarioBreakdownRowTestId(String(record[selectedKpiLevel] ?? '')),
                            } as React.HTMLAttributes<HTMLTableRowElement>)
                        }
                    />
                </Spin>
            </div>
        </div>
    );
};

KpiBreakdownTable.displayName = 'KpiBreakdownTable';
export default KpiBreakdownTable;
